import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * UiOverlayContext — small counter of currently-open modals/sheets.
 * Components register/unregister themselves so the FAB knows to hide.
 *
 * Usage in a Dialog wrapper:
 *   useOverlayLock(open);   // increments count while `open` is true
 */
const UiOverlayContext = createContext({
  hasOverlay: false,
  register: () => () => {},
});

export function UiOverlayProvider({ children }) {
  const [count, setCount] = useState(0);

  const register = useCallback(() => {
    setCount((c) => c + 1);
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      setCount((c) => Math.max(0, c - 1));
    };
  }, []);

  const value = useMemo(
    () => ({ hasOverlay: count > 0, register }),
    [count, register]
  );

  return (
    <UiOverlayContext.Provider value={value}>
      {children}
    </UiOverlayContext.Provider>
  );
}

export function useUiOverlay() {
  return useContext(UiOverlayContext);
}

/** Increments the overlay counter while `open` is truthy. */
export function useOverlayLock(open) {
  const { register } = useUiOverlay();
  const unregRef = useRef(null);

  useEffect(() => {
    if (open) {
      unregRef.current = register();
    } else if (unregRef.current) {
      unregRef.current();
      unregRef.current = null;
    }
    return () => {
      if (unregRef.current) {
        unregRef.current();
        unregRef.current = null;
      }
    };
  }, [open, register]);
}
